/**
 * <announcement-marquee>: continuously scrolling text for the announcement bar's "Scrolling text"
 * block (snippets/component-announcement-bar-block.liquid).
 *
 * The track holds two identical halves, each made of enough copies of the message group to cover
 * the visible width; CSS scrolls the track by exactly one half (-50%) so the loop has no seam.
 * The animation duration is derived from the half's pixel width and `data-speed` (pixels per
 * second), so text moves at the same pace at every screen width.
 */
if (!customElements.get('announcement-marquee')) {
  class AnnouncementMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.announcement-marquee__track');
      this.group = this.querySelector('.announcement-marquee__group');
      if (!this.track || !this.group) return;

      this.scheduleUpdate = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      // Also fires when a hidden breakpoint copy becomes visible.
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this);
      // Web fonts change the measured text width once they load.
      document.fonts?.ready.then(this.scheduleUpdate);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    update() {
      const visibleWidth = this.clientWidth;
      const groupWidth = this.group.getBoundingClientRect().width;
      if (!visibleWidth || !groupWidth) return;

      const groupsPerHalf = Math.max(1, Math.ceil(visibleWidth / groupWidth));
      const groupsNeeded = groupsPerHalf * 2;

      while (this.track.children.length < groupsNeeded) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        this.track.appendChild(clone);
      }
      while (this.track.children.length > groupsNeeded) {
        this.track.lastElementChild.remove();
      }

      const speed = parseFloat(this.dataset.speed) || 60;
      this.style.setProperty('--marquee-duration', `${(groupWidth * groupsPerHalf) / speed}s`);
      this.classList.add('is-ready');
    }
  }

  customElements.define('announcement-marquee', AnnouncementMarquee);
}
